// Tipagem para o styled-components Theme
import 'styled-components';
import { createGlobalStyle } from 'styled-components';

export type AppTheme = typeof theme;

declare module 'styled-components' {
  export interface DefaultTheme extends AppTheme {}
}

// Paleta light baseada na dark, mantendo harmonia e contraste
export const lightColors = {
  background: '#f3f4f8', // cinza claro sofisticado
  backgroundSecondary: '#ffffff', // cards: branco puro para contraste limpo
  backgroundTertiary: '#f7f8fb', // superfície levemente cinza para seções
  // Usa o mesmo vermelho do tema escuro para consistência visual
  primary: '#ff3b30', // vermelho consistente (mesmo do tema escuro)
  primaryHover: '#ff6259',
  primaryLight: '#ff7a72',
  primaryDark: '#d32f2f',
  // Ajustes para melhorar contraste nos cards em tema claro
  textPrimary: '#0f1720', // texto principal mais escuro e legível
  textSecondary: '#22242a', // texto secundário forte para títulos e labels
  textTertiary: '#4b4d59', // terciário mais contrastante para descrições
  textDisabled: '#9ea0a8',
  success: '#22c55e',
  warning: '#f59e42',
  error: '#ff3b30',
  info: '#007aff',
  border: '#d1d4dd', // bordas suaves mas visíveis
  borderLight: '#e9ebf2',
  borderDark: '#bdbfc6',
  overlay: 'rgba(255,255,255,0.95)',
  overlayLight: 'rgba(255,255,255,0.7)',
  gradientPrimary: 'linear-gradient(135deg, #f3f4f8 0%, #ff5a36 100%)',
  gradientSecondary: 'linear-gradient(135deg, #e9eaf3 0%, #f3f4f8 100%)',
  gradientNeural: 'linear-gradient(135deg, #f3f4f8 0%, #ff5a36 80%, #e9eaf3 100%)',
};

// Cores do tema futurista - Preto Profundo (Estilo OpenAI)
export const colors = {
  // Cores principais - Preto profundo elegante
  background: '#0A0A0A', // preto profundo, quase puro
  backgroundSecondary: '#151515', // preto profundo para cards e elevações
  backgroundTertiary: '#1A1A1A', // preto profundo para superfícies terciárias

  // Cores de acento vermelho (usadas só em botões, ícones, detalhes)
  primary: '#ff3b30', // vermelho mais limpo (Apple Red)
  primaryHover: '#ff6259',
  primaryLight: '#ff7a72',
  primaryDark: '#d32f2f',

  // Cores de texto - Maior contraste e suavidade
  textPrimary: '#FAFAFA', // branco quente, suave aos olhos
  textSecondary: '#A0A0A0', // cinza médio equilibrado
  textTertiary: '#707070', // cinza escuro sutil
  textDisabled: '#3A3A3A',

  // Cores de status
  success: '#00ff88', // verde para status de sucesso
  warning: '#ffaa00', // amarelo para warning
  error: '#ff3b30', // vermelho mais limpo para erro
  info: '#00aaff', // azul para info

  // Cores de borda e divisores - Sutis e elegantes
  border: '#2A2A2A', // borda sutil em preto profundo
  borderLight: '#353535',
  borderDark: '#1F1F1F',

  // Cores de overlay
  overlay: 'rgba(0, 0, 0, 0.92)',
  overlayLight: 'rgba(0, 0, 0, 0.65)',

  // Cores de gradiente
  gradientPrimary: 'linear-gradient(135deg, #151515 0%, #ff3b30 100%)', // vermelho só no final
  gradientSecondary: 'linear-gradient(135deg, #0A0A0A 0%, #151515 100%)',
  gradientNeural: 'linear-gradient(135deg, #151515 0%, #ff3b30 80%, #151515 100%)', // vermelho só como detalhe
};

// Tipografia
export const typography = {
  fontFamily: {
    primary: "'Inter', sans-serif",
    heading: "'Rajdhani', sans-serif",
  },
  fontSize: {
    xs: '0.75rem',
    sm: '0.875rem',
    base: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
    '2xl': '1.5rem',
    '3xl': '1.875rem',
    '4xl': '2.25rem',
    '5xl': '3rem',
  },
  fontWeight: {
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  lineHeight: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.75,
  },
};

// Espaçamentos
export const spacing = {
  0: '0',
  1: '0.25rem',
  2: '0.5rem',
  3: '0.75rem',
  4: '1rem',
  5: '1.25rem',
  6: '1.5rem',
  8: '2rem',
  10: '2.5rem',
  12: '3rem',
  14: '3.5rem',
  16: '4rem',
  20: '5rem',
  24: '6rem',
  32: '8rem',
};

// Bordas e sombras
export const borders = {
  radius: {
    none: '0',
    sm: '4px',      // Was 0.125rem (2px)
    base: '8px',    // Was 0.25rem (4px)
    md: '12px',     // Was 0.375rem (6px)
    lg: '16px',     // Was 0.5rem (8px)
    xl: '24px',     // Was 0.75rem (12px)
    '2xl': '32px',  // Was 1rem (16px)
    '3xl': '48px',  // New for large containers
    full: '9999px',
  },
  width: {
    none: '0',
    thin: '1px',
    medium: '2px',
    thick: '4px',
  },
};

export const shadows = {
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.10)',
  base: '0 1px 3px 0 rgba(0, 0, 0, 0.15), 0 1px 2px 0 rgba(0, 0, 0, 0.10)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.18), 0 2px 4px -1px rgba(0, 0, 0, 0.10)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.20), 0 4px 6px -2px rgba(0, 0, 0, 0.10)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.22), 0 10px 10px -5px rgba(0, 0, 0, 0.10)',
  neon: '0 0 8px rgba(255, 59, 48, 0.10), 0 0 16px rgba(255, 59, 48, 0.04)', // sombra vermelha mais limpa
  neonGreen: '0 0 20px rgba(0, 255, 136, 0.18), 0 0 40px rgba(0, 255, 136, 0.08)',
  neonBlue: '0 0 20px rgba(0, 170, 255, 0.18), 0 0 40px rgba(0, 170, 255, 0.08)',
};

// Animações
export const animations = {
  duration: {
    fast: '150ms',
    normal: '300ms',
    slow: '500ms',
  },
  easing: {
    easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
  },
};

// Breakpoints
export const breakpoints = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px',
};

// Tema completo

// Exportações de tema devem vir após todas as dependências
export const theme = {
  colors,
  typography,
  spacing,
  borders,
  shadows,
  animations,
  breakpoints,
  isLight: false,
};

export const themeLight = {
  colors: lightColors,
  typography,
  spacing,
  borders,
  shadows,
  animations,
  breakpoints,
  isLight: true,
};

// Estilos globais
export const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html {
    font-size: 16px;
    scroll-behavior: smooth;
  }

  body {
    font-family: ${typography.fontFamily.primary};
    font-size: ${typography.fontSize.base};
    font-weight: ${typography.fontWeight.normal};
    line-height: ${typography.lineHeight.normal};
    color: ${({ theme }) => theme.colors.textPrimary};
    background-color: ${({ theme }) => theme.colors.background};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    overflow-x: hidden;
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: ${typography.fontFamily.heading};
    font-weight: ${typography.fontWeight.semibold};
    line-height: ${typography.lineHeight.tight};
    color: ${colors.textPrimary};
  }

  h1 {
    font-size: ${typography.fontSize['4xl']};
  }

  h2 {
    font-size: ${typography.fontSize['3xl']};
  }

  h3 {
    font-size: ${typography.fontSize['2xl']};
  }

  h4 {
    font-size: ${typography.fontSize.xl};
  }

  h5 {
    font-size: ${typography.fontSize.lg};
  }

  h6 {
    font-size: ${typography.fontSize.base};
  }

  p {
    margin-bottom: ${spacing[4]};
    color: ${colors.textSecondary};
  }

  a {
    color: ${colors.primary};
    text-decoration: none;
    transition: color ${animations.duration.fast} ${animations.easing.easeInOut};

    &:hover {
      color: ${colors.primaryHover};
    }
  }

  button {
    font-family: inherit;
    cursor: pointer;
    border: none;
    outline: none;
    transition: all ${animations.duration.fast} ${animations.easing.easeInOut};
  }

  input, textarea, select {
    font-family: inherit;
    outline: none;
    transition: all ${animations.duration.fast} ${animations.easing.easeInOut};
  }

  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: ${colors.backgroundSecondary};
  }

  ::-webkit-scrollbar-thumb {
    background: ${colors.border};
    border-radius: ${borders.radius.full};
  }

  ::-webkit-scrollbar-thumb:hover {
    background: ${colors.borderLight};
  }

  /* Seleção de texto */
  ::selection {
    background-color: ${colors.primary};
    color: ${colors.textPrimary};
  }

  /* Placeholder */
  ::placeholder {
    color: ${colors.textTertiary};
    opacity: 1;
  }

  /* Focus visible */
  :focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
  }

  /* Loading spinner */
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  /* Pulse animation */
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }

  /* Glow animation */
  @keyframes glow {
    0%, 100% {
      box-shadow: 0 0 20px rgba(255, 68, 68, 0.3);
    }
    50% {
      box-shadow: 0 0 30px rgba(255, 68, 68, 0.6);
    }
  }

  /* Fade in animation */
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* Slide in animation */
  @keyframes slideIn {
    from {
      transform: translateX(-100%);
    }
    to {
      transform: translateX(0);
    }
  }

  /* Neural network pulse */
  @keyframes neuralPulse {
    0%, 100% {
      transform: scale(1);
      opacity: 0.8;
    }
    50% {
      transform: scale(1.05);
      opacity: 1;
    }
  }
`;

export default theme;

